import HeaderBar from "../../components/HeaderBar";
import { useState, useContext, useEffect, useMemo } from "react";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import {
  FaTrash,
  FaSave,
  FaArrowLeft,
  FaEdit,
  FaFilter,
  FaRedo,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/subAssemblyEntry";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const isNumericValue = (v) => {
  if (v === null || v === undefined) return false;
  if (typeof v === "number") return Number.isFinite(v);
  if (typeof v === "string") return v.trim() !== "" && !Number.isNaN(Number(v));
  return false;
};

const normalizeDDL = (rows, idKeys = [], nameKeys = []) => {
  const pick = (obj, keys) => {
    for (const k of keys) {
      if (obj?.[k] !== undefined && obj?.[k] !== null && String(obj[k]).trim() !== "") {
        return obj[k];
      }
    }
    return undefined;
  };

  return (Array.isArray(rows) ? rows : [])
    .map((r) => {
      const id = pick(r, idKeys);
      const name = pick(r, nameKeys) ?? "";
      return {
        id: id !== undefined ? String(id) : "",
        name: String(name || "").trim(),
        raw: r,
      };
    })
    .filter((x) => x.name);
};

const VehicleTypeMaintenanceEntry = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const vehicleTypeId = useMemo(
    () => toInt(searchParams.get("vehicletypeid")),
    [searchParams]
  );

  const loginUserId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";

  const branchId =
    Number(localStorage.getItem("branchId")) ||
    Number(localStorage.getItem("branchid")) ||
    8;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branch = localStorage.getItem("branchType") || localStorage.getItem("branchtype") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const [showFilters, setShowFilters] = useState(true);

  const [loadingDDL, setLoadingDDL] = useState(false);
  const [loadingList, setLoadingList] = useState(false);

  const [ddl, setDdl] = useState({
    vehicleType: { id: "", name: "" },
    subTypes: [],
    subAssemblies: [],
  });

  const [rows, setRows] = useState([]);

  const [editSelectedIds, setEditSelectedIds] = useState([]);
  const [deleteSelectedIds, setDeleteSelectedIds] = useState([]);

  const [form, setForm] = useState({
    maintenanceEntryId: 0,
    vehiclesubtypeid: "",
    vehiclesubassemblyid: "",
    description: "",
    partnumbergrade: "",
    specification: "",
    qty: "",
    uom: "",
    usagelimit: "",
  });

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const resetForm = () => {
    setForm({
      maintenanceEntryId: 0,
      vehiclesubtypeid: "",
      vehiclesubassemblyid: "",
      description: "",
      partnumbergrade: "",
      specification: "",
      qty: "",
      uom: "",
      usagelimit: "",
    });
  };

  const getRowId = (r) =>
    toInt(r.fld_vehicletypemaintenanceentryid || r.fld_vehicletypemaintenanceentryId || r.id);

  const fetchDropdowns = async () => {
    try {
      if (!vehicleTypeId) {
        toast.error("vehicletypeid missing in URL");
        return;
      }

      setLoadingDDL(true);

      const { data } = await axios.get(`${API_BASE}/dropdowns`, {
        params: { vehicletypeid: vehicleTypeId },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Dropdown fetch failed");
      }

      const vt = data?.data?.vehicleType || {};
      const vehicleType = {
        id: vt.id !== undefined ? String(vt.id) : String(vehicleTypeId),
        name: vt.name || String(vehicleTypeId),
      };

      const subTypes = normalizeDDL(
        data?.data?.subTypes,
        ["id", "fld_vehiclesubtypeid", "value", "Value"],
        ["name", "fld_vehiclesubtypename", "text", "Text"]
      );

      const subAssemblies = normalizeDDL(
        data?.data?.subAssemblies,
        ["id", "fld_vehiclesubassemblyid", "fld_vehiclesubassemblyid ", "value", "Value"],
        ["name", "fld_vehicle_subassemblyname", "fld_vehiclesubassemblyname", "text", "Text"]
      );

      setDdl({ vehicleType, subTypes, subAssemblies });
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to fetch dropdowns");
    } finally {
      setLoadingDDL(false);
    }
  };

  const fetchList = async () => {
    try {
      if (!vehicleTypeId) return;

      setLoadingList(true);

      const { data } = await axios.get(`${API_BASE}/list`, {
        params: { vehicletypeid: vehicleTypeId },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "List fetch failed");
      }

      setRows(Array.isArray(data?.data) ? data.data : []);
      setEditSelectedIds([]);
      setDeleteSelectedIds([]);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to fetch list");
      setRows([]);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    fetchDropdowns();
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleTypeId, commonHeaders]);

  const toggleEditSelect = (id) => {
    setEditSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleDeleteSelect = (id) => {
    setDeleteSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAllEdit = () => {
    if (!rows.length) return;
    if (editSelectedIds.length === rows.length) {
      setEditSelectedIds([]);
    } else {
      setEditSelectedIds(rows.map((r) => getRowId(r)).filter(Boolean));
    }
  };

  const toggleSelectAllDelete = () => {
    if (!rows.length) return;
    if (deleteSelectedIds.length === rows.length) {
      setDeleteSelectedIds([]);
    } else {
      setDeleteSelectedIds(rows.map((r) => getRowId(r)).filter(Boolean));
    }
  };

  const handleEdit = async (id) => {
    try {
      const { data } = await axios.get(`${API_BASE}/edit`, {
        params: { maintenanceEntryId: id },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Edit fetch failed");
      }

      const r = data.data || {};
      setForm({
        maintenanceEntryId: toInt(r.fld_vehicletypemaintenanceentryid || id),
        vehiclesubtypeid: r.fld_vehiclesubtypeid ? String(r.fld_vehiclesubtypeid) : "",
        vehiclesubassemblyid: r.fld_vehiclesubassemblyid ? String(r.fld_vehiclesubassemblyid) : "",
        description: r.fld_description || "",
        partnumbergrade: r.fld_partnumbergrade || "",
        specification: r.fld_specification || "",
        qty: r.fld_qty != null ? String(r.fld_qty) : "",
        uom: r.fld_uom || "",
        usagelimit: r.fld_usagelimit != null ? String(r.fld_usagelimit) : "",
      });

      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to load edit data");
    }
  };

  const handleEditSelected = async () => {
    if (!editSelectedIds.length) {
      toast.warn("Please select one record in Edit checkbox!");
      return;
    }

    if (editSelectedIds.length > 1) {
      toast.warn("Please select only one record to edit!");
      return;
    }

    const id = Number(editSelectedIds[0]) || 0;
    if (!id) {
      toast.error("Invalid selected id. Please refresh the page.");
      return;
    }

    await handleEdit(id);
  };

  const handleDelete = async (id) => {
    try {
      const ok = window.confirm("Delete this record?");
      if (!ok) return;

      const { data } = await axios.delete(`${API_BASE}/${id}`, {
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Delete failed");
      }

      toast.success(data?.message || "Deleted Successfully");
      fetchList();
      resetForm();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Delete failed");
    }
  };

  const handleDeleteSelected = async () => {
    if (!deleteSelectedIds.length) {
      toast.warn("Please select at least one record in Delete checkbox!");
      return;
    }

    try {
      const ok = window.confirm(`Delete ${deleteSelectedIds.length} selected record(s)?`);
      if (!ok) return;

      const { data } = await axios.post(
        `${API_BASE}/deleteMany`,
        { ids: deleteSelectedIds },
        { headers: commonHeaders }
      );

      if (!data?.success) {
        throw new Error(data?.message || "Bulk delete failed");
      }

      toast.success(data?.message || `Deleted ${data?.data?.deleted ?? 0} record(s)`);
      fetchList();
      resetForm();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Bulk delete failed");
    }
  };

  const resolveSubAssemblyId = () => {
    const v = form.vehiclesubassemblyid;

    if (isNumericValue(v)) return toInt(v);

    const byName = ddl.subAssemblies.find((x) => x.name === v);
    if (byName && isNumericValue(byName.id)) return toInt(byName.id);

    return 0;
  };

  const resolveSubTypeId = () => {
    const v = form.vehiclesubtypeid;
    if (v === "" || v === "Select") return null;
    if (isNumericValue(v)) return toInt(v);

    const byName = ddl.subTypes.find((x) => x.name === v);
    if (byName && isNumericValue(byName.id)) return toInt(byName.id);

    return null;
  };

  const handleSave = async () => {
    try {
      if (!vehicleTypeId) {
        toast.error("vehicletypeid missing in URL");
        return;
      }

      const subAssemblyId = resolveSubAssemblyId();
      if (!subAssemblyId) {
        toast.error("Sub Assembly ID missing from API. Please return id in /dropdowns response.");
        return;
      }

      const payload = {
        maintenanceEntryId: toInt(form.maintenanceEntryId),
        vehicletypeid: vehicleTypeId,
        vehiclesubtypeid: resolveSubTypeId(),
        vehiclesubassemblyid: subAssemblyId,
        description: form.description,
        partnumbergrade: form.partnumbergrade,
        specification: form.specification,
        qty: Number(form.qty),
        uom: form.uom,
        usagelimit: Number(form.usagelimit || 0),
        branchId,
        loginUserId,
      };

      if (!String(payload.description || "").trim()) {
        toast.error("Enter Description");
        return;
      }

      if (!String(payload.partnumbergrade || "").trim()) {
        toast.error("Enter Part Number/Grade");
        return;
      }

      if (!(payload.qty > 0)) {
        toast.error("Qty should be > 0");
        return;
      }

      if (!String(payload.uom || "").trim()) {
        toast.error("Enter UOM");
        return;
      }

      const { data } = await axios.post(`${API_BASE}/save`, payload, {
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Save failed");
      }

      toast.success(data?.message || "Saved successfully");
      fetchList();
      resetForm();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Save failed");
    }
  };

  return (
    <VehicleLayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">VEHICLE TYPE MAINTENANCE ENTRY</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `bg-white ${currentSkin.glass} text-black`
        }`}
      >
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4 flex-wrap">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Preventive Maintenance Entry
            </h1>

            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
              }`}
            >
              {rows.length} Records
            </span>

            {!vehicleTypeId && (
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700">
                vehicletypeid missing
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-800" +
                    (showFilters ? " bg-gray-800" : "")
                  : "border-gray-300 text-gray-700 hover:bg-gray-50" +
                    (showFilters ? " bg-gray-50" : "")
              }`}
            >
              <FaFilter className="w-4 h-4" />
              {showFilters ? "Hide Form" : "Show Form"}
            </button>

            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-white dark:hover:bg-gray-800"
            >
              <FaArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>
        </div>

        {showFilters && (
          <div
            className={`rounded-xl p-6 mb-6 border ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Vehicle Type<span className="text-red-500">*</span>
                </label>
                <input
                  value={ddl.vehicleType?.name || ""}
                  disabled
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Vehicle SubType
                </label>
                <select
                  name="vehiclesubtypeid"
                  value={form.vehiclesubtypeid}
                  onChange={onChange}
                  disabled={loadingDDL}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">Select</option>
                  {ddl.subTypes.map((x, idx) => (
                    <option key={x.id || `${x.name}-${idx}`} value={x.id || x.name}>
                      {x.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Sub Assembly<span className="text-red-500">*</span>
                </label>
                <select
                  name="vehiclesubassemblyid"
                  value={form.vehiclesubassemblyid}
                  onChange={onChange}
                  disabled={loadingDDL}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">Select</option>
                  {ddl.subAssemblies.map((x, idx) => (
                    <option key={x.id || `${x.name}-${idx}`} value={x.id || x.name}>
                      {x.name}
                    </option>
                  ))}
                </select>

                {form.vehiclesubassemblyid && !isNumericValue(form.vehiclesubassemblyid) && (
                  <div className="text-xs text-yellow-600 mt-1">
                    Note: selected value is not numeric (API might not be sending subAssembly id).
                  </div>
                )}
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Description<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="description"
                  value={form.description}
                  onChange={onChange}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Part Number/Grade<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="partnumbergrade"
                  value={form.partnumbergrade}
                  onChange={onChange}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Specification
                </label>
                <input
                  type="text"
                  name="specification"
                  value={form.specification}
                  onChange={onChange}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Qty<span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="qty"
                  value={form.qty}
                  onChange={onChange}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Usage Limit<span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="usagelimit"
                  value={form.usagelimit}
                  onChange={onChange}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  UOM<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="uom"
                  value={form.uom}
                  onChange={onChange}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>
            </div>

            <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={resetForm}
                className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200"
              >
                <FaRedo className="w-4 h-4" />
                Reset Form
              </button>

              <button
                onClick={handleSave}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200`}
              >
                <FaSave />
                {form.maintenanceEntryId ? "Update Record" : "Save Record"}
              </button>
            </div>
          </div>
        )}

        {(editSelectedIds.length > 0 || deleteSelectedIds.length > 0) && (
          <div
            className={`rounded-lg p-4 mb-6 ${
              isDark ? "bg-blue-900/30 border border-blue-700" : "bg-blue-50 border border-blue-200"
            }`}
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-4">
                <span className={`font-semibold ${isDark ? "text-blue-200" : "text-blue-800"}`}>
                  Edit Selected: {editSelectedIds.length}
                </span>
                <span className={`font-semibold ${isDark ? "text-blue-200" : "text-blue-800"}`}>
                  Delete Selected: {deleteSelectedIds.length}
                </span>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={handleEditSelected}
                  className={`px-4 py-2 text-white rounded-lg font-medium ${buttonClass} ${hoverClass}`}
                >
                  Edit
                </button>

                <button
                  onClick={handleDeleteSelected}
                  className="flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium bg-red-600 hover:bg-red-700"
                >
                  <FaTrash className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        <div
          className={`rounded-xl shadow-sm border overflow-hidden ${
            isDark ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={`${buttonClass} text-white`}>
                <tr>
                  <th className="px-4 py-4 text-left font-semibold">S.No</th>

                  <th className="px-4 py-4 text-left font-semibold">
                    <input
                      type="checkbox"
                      checked={rows.length > 0 && editSelectedIds.length === rows.length}
                      onChange={toggleSelectAllEdit}
                      className="mr-2"
                    />
                    Edit
                  </th>

                  <th className="px-4 py-4 text-left font-semibold">
                    <input
                      type="checkbox"
                      checked={rows.length > 0 && deleteSelectedIds.length === rows.length}
                      onChange={toggleSelectAllDelete}
                      className="mr-2"
                    />
                    Delete
                  </th>

                  <th className="px-4 py-4 text-left font-semibold">Sub Assembly</th>
                  <th className="px-4 py-4 text-left font-semibold">Sub Type</th>
                  <th className="px-4 py-4 text-left font-semibold">Description</th>
                  <th className="px-4 py-4 text-left font-semibold">Part No/Grade</th>
                  <th className="px-4 py-4 text-left font-semibold">Qty</th>
                  <th className="px-4 py-4 text-left font-semibold">UOM</th>
                  <th className="px-4 py-4 text-left font-semibold">Usage</th>
                  <th className="px-4 py-4 text-left font-semibold">Action</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {loadingList ? (
                  Array.from({ length: 6 }).map((_, idx) => (
                    <tr key={idx} className={isDark ? "bg-gray-800" : "bg-white"}>
                      {Array.from({ length: 11 }).map((__, i) => (
                        <td key={i} className="px-4 py-3">
                          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="text-center py-8 text-gray-500">
                      No records found.
                    </td>
                  </tr>
                ) : (
                  rows.map((r, idx) => {
                    const id = getRowId(r);

                    return (
                      <tr
                        key={id || idx}
                        className={`hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200 ${
                          isDark ? "bg-gray-900" : "bg-white"
                        }`}
                      >
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                          {idx + 1}
                        </td>

                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={editSelectedIds.includes(id)}
                            onChange={() => toggleEditSelect(id)}
                          />
                        </td>

                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={deleteSelectedIds.includes(id)}
                            onChange={() => toggleDeleteSelect(id)}
                          />
                        </td>

                        <td className="px-4 py-3">
                          <span className="text-blue-600 dark:text-blue-400 font-medium">
                            {r.fld_vehicle_subassemblyname || "-"}
                          </span>
                        </td>

                        <td className="px-4 py-3">{r.fld_vehiclesubtypename || "-"}</td>
                        <td className="px-4 py-3">{r.fld_description || "-"}</td>
                        <td className="px-4 py-3">{r.fld_partnumbergrade || "-"}</td>
                        <td className="px-4 py-3">{r.fld_qty ?? "-"}</td>
                        <td className="px-4 py-3">{r.fld_uom || "-"}</td>
                        <td className="px-4 py-3">{r.fld_usagelimit ?? "-"}</td>

                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEdit(id)}
                              className={`px-3 py-1 text-white rounded-lg text-sm font-medium ${buttonClass} ${hoverClass}`}
                            >
                              <span className="flex items-center gap-2">
                                <FaEdit className="w-3 h-3" />
                                Edit
                              </span>
                            </button>

                            <button
                              onClick={() => handleDelete(id)}
                              className="px-3 py-1 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700"
                            >
                              <span className="flex items-center gap-2">
                                <FaTrash className="w-3 h-3" />
                                Delete
                              </span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          theme={isDark ? "dark" : "light"}
        />
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleTypeMaintenanceEntry;